import { TSelectOptionTypeUi } from 'components/Select';
import { TOptionTypeUi } from './general.types';
import { TYesNoOptionType } from './generalTypes';

export type TResidentsPetChargesUploadDataUI = {
  propertyId: string;
  skipMovedOutTenantsAndAnimals?: boolean;
  importType: TOptionTypeUi;
  tenantsFile: File[];
  petChargesFile: File[];
  shouldConsiderMoveOut: string;
  shouldConsiderMissingProperties?: string;
};

export type TIntegrationDataUI = {
  skipMovedOutTenantsAndAnimals?: boolean;
  type: TOptionTypeUi;
  shouldConsiderMoveOut: string;
  shouldConsiderMissingProperties?: string;
  csvFile: File[];
};

type TPreviewResidencyDataProps = {
  numberOfAnimals: number;
  numberOfResidencies: number;
};

type TPreviewPropertyDetailProps = {
  numberOfProperties: number;
  numberOfResidencies: number;
  numberOfApplicants: number;
};

export type TIntegrationResultProps = {
  numberOfNewApplicantAnimals: number;
  foundMovedOutAnimalsFile: string | null;
  numberOfApplicantsToMove: number;
  skippedNotFoundPropertyApplications: TPreviewPropertyDetailProps;
  numberOfFoundMovedOutAnimals: number;
  numberOfFoundMovedOutTenants: number;
  applicantAnimalsFile: string | null;
  applicants: TPreviewResidencyDataProps;
  applicantsFile: string | null;
  foundMovedOutTenantsFile: string | null;
  moveInResidencies: TPreviewResidencyDataProps;
  moveOutResidencies: TPreviewResidencyDataProps;
  numberOfMatchedProperties: number;
  numberOfNewAnimals: number;
  numberOfResidencesUnableToParse: number;
  skippedNotFoundPropertyResidences: TPreviewPropertyDetailProps;
  terminatingMissingPropertyResidences: TPreviewPropertyDetailProps;
  numberOfAnimalsSkippedAsDuplicate: number;
  skippedResidenciesCountBecauseOfTenantDuplication: number;
  numberOfResidenciesToMove: number;
  moveInResidenciesFile: string | null;
  moveInAnimalsFile: string | null;
  moveOutFile: string | null;
  moveResidenciesFile: string | null;
  numberOfPropertiesToOnboard: number;
  numberOfResidenciesToTagAsKnown: number;
  numberOfAnimalsToTagAsKnown: number;
};

export type TIntegrationResultPropsUI = {
  numberOfNewApplicantAnimals: number;
  foundMovedOutAnimalsFile: Blob | null;
  numberOfFoundMovedOutAnimals: number;
  numberOfFoundMovedOutTenants: number;
  foundMovedOutTenantsFile: Blob | null;
  applicantAnimalsFile: Blob | null;
  applicantsResidencies: number;
  applicantsAnimals: number;
  applicantsFile: Blob | null;
  movedInResidencies: number;
  movedInTotalAnimals: number;
  movedInAnimals: number;
  numberOfNewAnimals: number;
  movedOutTotalResidencies: number;
  movedOutResidencies: number;
  movedOutAnimals: number;
  movedOutResidenciesFromMissingProperty: number;
  numberOfMissingProperties: number;
  numberOfMatchedProperties: number;
  numberOfSkippedResidencies: number;
  numberOfSkippedProperties: number;
  numberOfSkippedApplicants: number;
  numberOfSkippedApplicantsProperties: number;
  numberOfAnimalsSkippedAsDuplicate: number;
  skippedResidenciesCountBecauseOfTenantDuplication: number;
  numberOfResidenciesToMove: number;
  numberOfApplicantsToMove: number;
  moveInResidenciesFile: Blob | null;
  moveInAnimalsFile: Blob | null;
  moveOutFile: Blob | null;
  moveResidenciesFile: Blob | null;
  numberOfPropertiesToOnboard: number;
  numberOfResidenciesToTagAsKnown: number;
  numberOfAnimalsToTagAsKnown: number;
};

export type TIntegrationExecutionResultProps = {
  matchedPropertiesMoveInResidencies: number;
  matchedPropertiesMoveOutResidencies: number;
  terminatingMissingPropertyResidences: number;
};

export type TIntegrationType = 'APFOLIO' | 'REAL_PAGE' | 'YARDI' | null;

export type TSycnScheduleDB = {
  residentSyncPeriodicity: TSyncPeriodicityDB;
  newApplicantsSyncPeriodicity: TSyncPeriodicityDB;
  applicantsUpdateSyncPeriodicity: TSyncPeriodicityDB;
};

// Base sync schedule type (used by Entrata)
export type TSycnScheduleUI = {
  residents: TSelectOptionTypeUi;
  newApplicants: TSelectOptionTypeUi;
  applicantsUpdate: TSelectOptionTypeUi;
  residentsQuantity?: TSelectOptionTypeUi;
  newApplicantsUnit?: TSelectOptionTypeUi;
  newApplicantsQuantity?: TSelectOptionTypeUi;
  applicantsUpdateUnit?: TSelectOptionTypeUi;
  applicantsUpdateQuantity?: TSelectOptionTypeUi;
  petRentChargeCodes?: string[];
  petFeeChargeCodes?: string[];
};

// Document push configuration fields shared by Yardi and Entrata
export type TDocumentPushConfigFields = {
  documentPushBackConfiguration: EPushConfigurationTypes;
  residentAttachmentType: string;
  guestAttachmentType?: string;
};

// Yardi-specific sync schedule type with document push configuration
export type TYardiSyncScheduleUI = TSycnScheduleUI & TDocumentPushConfigFields;

// Entrata-specific sync schedule type (no guestAttachmentType - not supported by Entrata BE)
export type TEntrataSyncScheduleUI = TSycnScheduleUI & Omit<TDocumentPushConfigFields, 'guestAttachmentType'>;

export type TYardiChargeCodesDB = {
  petRentChargeCodes?: string[];
  petFeeChargeCodes?: string[];
} & TDocumentPushConfigFields;

export type TYardiChargeCodesUI = {
  petRentChargeCodes?: string[];
  petFeeChargeCodes?: string[];
} & TDocumentPushConfigFields;
export type TSyncPeriodicityDB = {
  unit: ESycnPeriodicityDB;
  quantity: number;
};
export type TSyncPeriodicityUI = {
  unit: ESycnPeriodicityUI;
  quantity: number;
  previewText?: string;
};

export enum EPushConfigurationTypes {
  DISABLED = 'DISABLED',
  PUSH_REQUIRED_DOCUMENTS = 'PUSH_REQUIRED_DOCUMENTS',
}

export type TConnectYardiIntegrationDB = {
  username: string;
  password: string;
  database: string;
  baseUrl: string;
  isTestMode: boolean;
  petRentChargeCodes?: string[];
  petFeeChargeCodes?: string[];
} & TDocumentPushConfigFields;

export type TConnectYardiIntegrationUI = {
  username: string;
  password: string;
  database: string;
  baseUrl: string;
  petRentChargeCodes?: string[];
  petFeeChargeCodes?: string[];
  isTestMode?: boolean;
} & TDocumentPushConfigFields;

export type TYardiVersionsDB = {
  coreInterfaceVersion: string;
  pluginVersion: string;
  voyagerVersion: string;
};
export type TYardiVersionsUI = {
  coreInterfaceVersion: string;
  pluginVersion: string;
  voyagerVersion: string;
};

export type TPetChargeSyncEnableValidateDB = {
  chargeCodeInfo: {
    petFeeChargeCodes: string[];
    petRentChargeCodes: string[];
  };
  policyInfo: {
    petFeeChargeAmount: number;
    petRentChargeAmount: boolean;
  };
};

export type TPetChargeSyncEnableValidateUI = {
  canEnable: boolean;
  chargeCodesErrorStr?: string;
  isPolicyError: boolean;
  chargeCodeInfo: {
    petFeeChargeCodes: string[];
    petRentChargeCodes: string[];
  };
  policyInfo: {
    petFeeChargeAmount: number;
    petRentChargeAmount: boolean;
  };
};

export type TYardiIntegrationSettingsDB = {
  canConnect: boolean;
  settings: TYardiIntegrationDB[] | null;
};
export type TYardiIntegrationSettingsUI = {
  canConnect: boolean;
  settings: TYardiIntegrationUI[] | null;
};

export type TYardiIntegrationDB = {
  applicantsUpdateSyncPeriodicity: TSyncPeriodicityDB | null;

  database: string | null;
  id: string | null;
  newApplicantsSyncPeriodicity: TSyncPeriodicityDB | null;
  residentSyncPeriodicity: TSyncPeriodicityDB | null;
  username: string | null;
  baseUrl: string | null;
  ilgGuestCardVersion: TYardiVersionsDB;
  residentDataVersion: TYardiVersionsDB;
  petFeeChargeCodes: string[];
  petRentChargeCodes: string[];
  isTestMode: boolean;
} & TDocumentPushConfigFields;

export type TYardiIntegrationUI = {
  applicantsUpdateSyncPeriodicity: TSyncPeriodicityUI | null;
  database: string;
  id: string | null;
  newApplicantsSyncPeriodicity: TSyncPeriodicityUI | null;
  password: string;
  residentSyncPeriodicity: TSyncPeriodicityUI | null;
  username: string;
  baseUrl: string;
  ilgGuestCardVersion: TYardiVersionsUI | null;
  residentDataVersion: TYardiVersionsUI | null;
  petFeeChargeCodes: string[] | null;
  petRentChargeCodes: string[] | null;
  isTestMode: boolean;
} & TDocumentPushConfigFields;

export enum ESyncStatusDB {
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
  IN_PROGRESS = 'IN_PROGRESS',
  QUEUED = 'QUEUED',
}

export enum ESyncStatusUI {
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
  IN_PROGRESS = 'IN_PROGRESS',
  QUEUED = 'QUEUED',
}
export type TIntegrationPropertyDB = {
  id: string;
  name: string;
  propertyMatched: false;
  state: string;
  unitsCount: number;
  applicantsSyncEnabled: boolean;
  residentsSyncEnabled: boolean;
  guestCardEventPushBackEnabled?: boolean | null;
  guestCardAgentName?: string | null;
  city: string;
  policyId: string;
  address1: string;
  zip: string;
  existingApplicantsLastSyncTime: string;
  newApplicantsLastSyncTime: string;
  residentsLastSyncTime: string;
  canPlanSync: boolean;
  matchedProperty: string | null;
  settingsDatabase?: string;
  settingsId: string;
  organizationId?: string;
  residentsSyncStatus: ESyncStatusDB | null;
  newApplicantsSyncStatus: ESyncStatusDB | null;
  existingApplicantsSyncStatus: ESyncStatusDB | null;
  residentsSyncFailReason?: string | null;
  newApplicantsSyncFailReason?: string | null;
  existingApplicantsSyncFailReason?: string | null;
};
export type TIntegrationPropertyUI = {
  canPlanSync: boolean;
  id: string;
  name: string;
  propertyMatched: false;
  state: string;
  unitsCount: number;
  isSyncEnabled: boolean;
  applicantsSyncEnabled: boolean;
  residentsSyncEnabled: boolean;
  guestCardEventPushBackEnabled?: TYesNoOptionType | null;
  guestCardAgentName?: string | null;
  city: string;
  policyId: string;
  address1: string;
  zip: string;
  existingApplicantsLastSyncTime?: string;
  newApplicantsLastSyncTime?: string;
  residentsLastSyncTime?: string;
  matchedProperty: string | null;
  settingsDatabase?: string;
  organizationId?: string;
  settingsId: string;
  residentsSyncStatus: ESyncStatusUI | null;
  newApplicantsSyncStatus: ESyncStatusUI | null;
  existingApplicantsSyncStatus: ESyncStatusUI | null;
  residentsSyncFailReason?: string | null;
  newApplicantsSyncFailReason?: string | null;
  existingApplicantsSyncFailReason?: string | null;
  residentsLastSyncDate?: string;
  newApplicantsLastSyncDate?: string;
  existingApplicantsLastSyncDate?: string;
};

export enum ESycnPeriodicityDB {
  DAILY = 'DAYS',
  WEEKLY = 'WEEKS',
  MONTHLY = 'MONTHS',
  // MINUTES = 'MINUTES',
  HOURS = 'HOURS',
}

export enum ESycnPeriodicityUI {
  DAILY = 'Daily',
  WEEKLY = 'Weekly',
  MONTHLY = 'Monthly',
  // MINUTES = 'Minutes',
  HOURS = 'Hourly',
}

export type TPropertyForMatchDB = {
  hasAssignedPolicy: boolean;
  id: string;
  name: string;
  policyId: string | null;
};
export type TPropertyForMatchUI = {
  defaultPolicy: boolean;
  label: string;
  value: string;
  policyId: string | null;
};

export type TPropertyMatchDB = {
  propertyId: string;
  linkedPropertyId: string;
  applicantsEnabled: boolean;
  residentsEnabled: boolean;
};
export type TPropertyMatchUI = {
  propertyId: string;
  residentsEnabled?: boolean;
  applicantsEnabled?: boolean;
  matchedProperty: TPropertyForMatchUI;
  newPropertyId?: string;
  animalRequirementsSetId?: TOptionTypeUi;
};

// Yardi-only extension: includes the guest card events push back fields.
// These are sent only when applicantsEnabled is true.
export type TYardiPropertyMatchDB = TPropertyMatchDB & {
  guestCardEventPushBackEnabled?: boolean;
  guestCardAgentName?: string | null;
};
export type TYardiPropertyMatchUI = TPropertyMatchUI & {
  guestCardEventPushBackEnabled?: TYesNoOptionType;
  guestCardAgentName?: string;
};

export type TPropertySyncStateDB = {
  propertyId: string;
  applicantsEnabled?: boolean;
  residentsEnabled: boolean;
};
export type TPropertySyncStateUI = {
  propertyId: string;
  applicantsEnabled: boolean;
  residentsEnabled: boolean;
};

export type TYardiPropertySyncStateDB = TPropertySyncStateDB & {
  guestCardEventPushBackEnabled?: boolean;
  guestCardAgentName?: string | null;
};
export type TYardiPropertySyncStateUI = TPropertySyncStateUI & {
  guestCardEventPushBackEnabled?: TYesNoOptionType;
  guestCardAgentName?: string;
};

export type TPropertySyncStateBulkDB = {
  /**
   * Backend expects integration-specific property ID field names (yardiPropertyIds or entrataPropertyIds).
   * The UI uses a generic `propertyIds` field, and converters map it to the appropriate field.
   */
  yardiPropertyIds?: string[];
  entrataPropertyIds?: string[];
  applicantsEnabled?: boolean;
  residentsEnabled: boolean;
};
export type TPropertySyncStateBulkUI = {
  propertyIds: string[];
  applicantsEnabled: boolean;
  residentsEnabled: boolean;
};

export type TYardiPropertySyncStateBulkDB = TPropertySyncStateBulkDB & {
  guestCardEventPushBackEnabled?: boolean;
  guestCardAgentName?: string | null;
};
export type TYardiPropertySyncStateBulkUI = TPropertySyncStateBulkUI & {
  guestCardEventPushBackEnabled?: TYesNoOptionType;
  guestCardAgentName?: string;
};

export type TResmanConnectDB = {
  resManId: string;
  syncPeriodicity?: {
    unit: string;
    quantity: number;
  };
};
export type TResmanConnectUI = {
  resManId: string;
  syncPeriodicity?: TOptionTypeUi;
  settingsId?: string | null;
};

export type TResmanIntegrationDB = {
  accountId: string | null;
  canConnect: boolean | null;
  id: string | null;
  syncPeriodicity: {
    unit: string;
    quantity: number;
  };
};
export type TResmanIntegrationUI = {
  accountId?: string;
  canConnect: boolean;
  id?: string;
  syncPeriodicity?: TSelectOptionTypeUi;
};

export type TResmanPropertiesDB = {
  id: string;
  name: string;
  address1: string;
  city: string;
  unitCount: number;
  type: string;
  status: string;
  state: string;
  isStateSupported: boolean;
  lastSyncDate: string | null;
  policy: string | null;
  propertyMatched: boolean;
  enabled: boolean;
  matchedProperty: string | null;
  canPlanSync: boolean;
};
export type TResmanPropertiesUI = {
  id: string;
  canPlanSync: boolean;
  name: string;
  address1: string;
  city: string;
  unitsCount: number;
  type: string;
  status: string;
  state: string;
  isStateSupported: boolean;
  residentsLastSyncTime?: string;
  policy: string | null;
  propertyMatched: boolean;
  isSyncEnabled: boolean;
  residentsSyncEnabled: boolean;
  matchedProperty: string | null;
};

export type TResmanPropertyMatchDB = {
  propertyId: string;
  linkedPropertyId: string;
  residentsEnabled: boolean;
};
export type TResmanPropertyMatchUI = {
  propertyId: string;
  linkedPropertyId: string;
  residentsEnabled: boolean;
};

export type TResmanPropertySyncStateDB = {
  propertyId: string;
  residentsEnabled: string;
};

export type TResmanPropertySyncStateUI = {
  propertyId: string;
  residentsEnabled: string;
};

export enum EIntegrationType {
  YARDI = 'Yardi',
  RESMAN = 'Resman',
  REAL_PAGE = 'Real Page',
  ENTRATA = 'Entrata',
  APFOLIO = 'AppFolio',
}
export enum EIntegrationTypeDB {
  YARDI = 'YARDI',
  RESMAN = 'RESMAN',
  REAL_PAGE = 'REAL_PAGE',
  ENTRATA = 'ENTRATA',
  APFOLIO = 'APFOLIO',
}

// Integrations fed by a PM-uploaded zip of report exports rather than a vendor API.
export type TZipUploadIntegrationType = EIntegrationTypeDB.REAL_PAGE | EIntegrationTypeDB.APFOLIO;

export type TIntegrationDataExportUI = {
  propertyId: string;
  propertyName: string;
  propertyManagementId: string;
  integrationType: 'Yardi' | 'Resman' | 'Entrata';
};

export type TIntegrationDataExportDB = {
  propertyManagementId: string;
  yardiPropertyId?: string;
  resManPropertyId?: string;
};

export type TUploadIntegrationSettingsDB = {
  id?: string;
  canConnect: boolean;
  isConnected: boolean;
  lastUploadDate?: string;
};
export type TUploadIntegrationSettingsUI = {
  id?: string;
  canConnect: boolean;
  isConnected: boolean;
  lastUploadDate?: string;
};

export type TTPConnectDB = {
  type: EIntegrationTypeDB;
  settings?: null; // TODO update this to integration setting when endpoinds are changed
};
export type TTPConnectUI = {
  type: EIntegrationTypeDB;
  settings?: null;
};

export type TTPDisconnectDB = {
  thirdPartySettingsId: string;
  type: EIntegrationTypeDB;
};
export type TTPDisconnectUI = {
  thirdPartySettingsId: string;
  type: EIntegrationTypeDB;
};

export type TPUploadSyncUI = {
  type: EIntegrationTypeDB;
  zipFile: File[];
};

export type TZipUploadUI = {
  zipFile: File[];
};

export type TTPPropertiesDB = {
  address1?: string;
  city?: string;
  id: string;
  integrationType: string;
  lastSuccessfulSyncDate?: string;
  linkedProperty?: { propertyId: string; name: string };
  matchedProperty: boolean;
  name: string;
  state?: string;
  syncEnabled: boolean;
  syncStatus?: string;
  failReason?: string | null;
  unitsCount?: number;
  zip?: string;
  canEnableSync: boolean;
  canStartSync: boolean;
};

export type TTPPropertiesUI = {
  address1?: string;
  city?: string;
  id: string;
  integrationType: string;
  lastSuccessfulSyncDate?: string;
  linkedProperty?: { propertyId: string; name: string };
  matchedProperty: boolean;
  name: string;
  state?: string;
  syncEnabled: boolean;
  syncStatus?: string;
  failReason?: string | null;
  unitsCount?: number;
  zip?: string;
};

export type TTPSyncStateProps = {
  thirdPartyPropertyId: string;
  integrationType: string;
};

export type TTPPropertyLink = {
  integrationType: string;
  thirdPartyPropertyId: string;
  propertyId: string;
};
export type TTPPropertyToLinkDB = {
  id: string;
  name: string;
  hasAssignedPolicy: boolean;
};

export type TTPPropertyToLinkUI = {
  value: string;
  label: string;
  hasAssignedPolicy: boolean;
};

// ============ ENTRATA TYPES ============

// GET /integration/entrata - wrapper response (supports multiple connections)
export type TEntrataIntegrationSettingsDB = {
  canConnect: boolean;
  settings: TEntrataIntegrationDB[] | null;
};

export type TEntrataIntegrationSettingsUI = {
  canConnect: boolean;
  settings: TEntrataIntegrationUI[] | null;
};

// Single Entrata connection
export type TEntrataIntegrationDB = {
  id: string | null;
  organizationId: string | null;
  residentSyncPeriodicity: TSyncPeriodicityDB | null;
  newApplicantsSyncPeriodicity: TSyncPeriodicityDB | null;
  applicantsUpdateSyncPeriodicity: TSyncPeriodicityDB | null;
  petRentChargeCodes: string[];
  petFeeChargeCodes: string[];
  documentPushBackConfiguration: EPushConfigurationTypes;
  residentAttachmentType: string;
};

export type TEntrataIntegrationUI = {
  id: string | null;
  organizationId: string;
  residentSyncPeriodicity: TSyncPeriodicityUI | null;
  newApplicantsSyncPeriodicity: TSyncPeriodicityUI | null;
  applicantsUpdateSyncPeriodicity: TSyncPeriodicityUI | null;
  petRentChargeCodes: string[];
  petFeeChargeCodes: string[];
  documentPushBackConfiguration: EPushConfigurationTypes;
  residentAttachmentType: string;
};

// POST /integration/entrata - connect (wrapped in connectEntrataDtos array)
export type TConnectEntrataIntegrationDB = {
  organizationId: string;
  petRentChargeCodes?: string[];
  petFeeChargeCodes?: string[];
  documentPushBackConfiguration: EPushConfigurationTypes;
  residentAttachmentType: string;
};

export type TConnectEntrataIntegrationUI = {
  organizationId: string;
  petRentChargeCodes?: string[];
  petFeeChargeCodes?: string[];
  documentPushBackConfiguration: EPushConfigurationTypes;
  residentAttachmentType: string;
};

// PUT /integration/entrata - update charge codes
export type TUpdateEntrataIntegrationDB = {
  entrataSettingsId: string;
  petRentChargeCodes?: string[];
  petFeeChargeCodes?: string[];
  documentPushBackConfiguration: EPushConfigurationTypes;
  residentAttachmentType: string;
};

export type TUpdateEntrataIntegrationUI = {
  entrataSettingsId: string;
  petRentChargeCodes?: string[];
  petFeeChargeCodes?: string[];
  documentPushBackConfiguration: EPushConfigurationTypes;
  residentAttachmentType: string;
};

// DELETE /integration/entrata
export type TDeleteEntrataIntegrationDB = {
  settingsId: string;
};
